import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { AuthGuard } from '@/components/AuthGuard';
import { Layout } from '@/components/Layout';
import { useMe } from '@/hooks/useMe';
import { useOnboardingGate } from '@/hooks/useOnboardingGate';
import { ConnectionsPage } from '@/pages/ConnectionsPage';
import { RulesPage } from '@/pages/RulesPage';
import { RuleCreatePage } from '@/pages/RuleCreatePage';
import { RuleDetailPage } from '@/pages/RuleDetailPage';
import { OnboardingPage } from '@/pages/OnboardingPage';
import { AdminLanding } from '@/pages/admin/AdminLanding';
import { AdminProviderConfigsPage } from '@/pages/admin/AdminProviderConfigsPage';
import { AdminUsersPage } from '@/pages/admin/AdminUsersPage';
import { AdminAuditPage } from '@/pages/admin/AdminAuditPage';
import { NotFoundPage } from '@/pages/NotFoundPage';

function AdminRoute({ children }: { children: React.ReactNode }) {
  const { data: me } = useMe();
  if (me && !me.is_admin) return <Navigate to="/connections" replace />;
  return <>{children}</>;
}

function AppRoutes() {
  const { data: me } = useMe();
  const onboarding = useOnboardingGate();
  const location = useLocation();
  if (!me) return null; // AuthGuard handles the loading/redirect state.

  // Forced onboarding: until they have a connection, every route except
  // /onboarding is redirected through the wizard. Admin routes are exempt so
  // an admin can configure provider configs before any user can onboard.
  if (
    onboarding.needsOnboarding &&
    location.pathname !== '/onboarding' &&
    !location.pathname.startsWith('/admin')
  ) {
    return <Navigate to="/onboarding" replace />;
  }

  return (
    <Layout me={me}>
      <Routes>
        <Route path="/" element={<Navigate to="/connections" replace />} />
        <Route path="/connections" element={<ConnectionsPage />} />
        <Route path="/onboarding" element={<OnboardingPage />} />
        <Route path="/rules" element={<RulesPage />} />
        <Route path="/rules/new" element={<RuleCreatePage />} />
        <Route path="/rules/:ruleId" element={<RuleDetailPage />} />
        <Route
          path="/admin"
          element={
            <AdminRoute>
              <AdminLanding />
            </AdminRoute>
          }
        />
        <Route
          path="/admin/providers"
          element={
            <AdminRoute>
              <AdminProviderConfigsPage />
            </AdminRoute>
          }
        />
        <Route
          path="/admin/users"
          element={
            <AdminRoute>
              <AdminUsersPage />
            </AdminRoute>
          }
        />
        <Route
          path="/admin/audit"
          element={
            <AdminRoute>
              <AdminAuditPage />
            </AdminRoute>
          }
        />
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </Layout>
  );
}

export function App() {
  return (
    <AuthGuard>
      <AppRoutes />
    </AuthGuard>
  );
}
